import { ReactNode } from 'react'
import {
  MdCheckCircleOutline,
  MdClose,
  MdErrorOutline,
  MdInfoOutline,
  MdWarningAmber,
} from 'react-icons/md'

export type TToastNotificationType = 'success' | 'warning' | 'error' | 'info'

export type TToastNotification = {
  readonly id?: string
  readonly show: boolean
  readonly type: TToastNotificationType
  readonly title: string
  readonly content?: ReactNode
  readonly traceId?: string
  readonly disableClose?: boolean
  readonly disableIcon?: boolean
  readonly onClose?: () => void
}

export default function ToastNotification({
  id,
  show,
  type = 'success',
  title,
  content,
  traceId,
  disableClose,
  disableIcon,
  onClose,
}: TToastNotification) {
  const typeClass = {
    success: 'border-pop-green-500 bg-pop-green-50',
    warning: 'border-pop-yellow-200 bg-pop-yellow-50',
    error: 'border-pop-red-500 bg-pop-red-50',
    info: 'border-pop-blue-500 bg-pop-blue-50 text-pop-blue-700',
  }[type]

  const iconType = {
    success: (
      <MdCheckCircleOutline
        data-testid='toast-icon-success'
        className='h-6 w-6 text-pop-green-600'
      />
    ),
    warning: (
      <MdWarningAmber data-testid='toast-icon-warning' className='h-6 w-6 text-pop-yellow-300' />
    ),
    error: <MdErrorOutline data-testid='toast-icon-error' className='h-6 w-6 text-pop-red-500' />,
    info: <MdInfoOutline data-testid='toast-icon-info' className='h-6 w-6 text-pop-blue-600' />,
  }[type]

  return (
    <div
      id={`toast_${id}`}
      data-testid='toast-container'
      className={`fixed bottom-0 left-0 right-0 z-[999] mx-auto flex flex-col gap-4 px-5 py-4 md:bottom-auto md:left-auto md:right-5 md:top-5 md:w-1/2
                  md:py-6 lg:bottom-auto lg:left-auto lg:right-5 lg:top-5 lg:w-1/2 lg:py-9 xl:w-1/3`}
    >
      <div
        className={`${
          show ? 'flex' : 'hidden'
        } flex-row gap-3 rounded-2xl border ${typeClass} p-5 shadow-lg`}
      >
        {!disableIcon && iconType}
        <figure className='flex-grow'>
          <figcaption className='font-semibold'>{title}</figcaption>
          {!!content && content}
          {!!traceId && (
            <p className='mt-2 text-xs font-light text-pop-grey-500'>Trace ID: {traceId}</p>
          )}
        </figure>
        {!disableClose && (
          <MdClose
            className='mb-auto ml-auto h-6 w-6 cursor-pointer text-pop-grey-600 lg:flex'
            onClick={onClose}
            data-testid='toast-close-button'
            width={24}
            height={24}
          />
        )}
      </div>
    </div>
  )
}
